import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';
import type { SubscriptionLicense } from 'src/api/subscriptionsApi';
import CustomizedTable from 'src/components/tables/CustomizedTable';

const DEFAULT_PAGE_SIZE = 25;

const formatLicenseDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

interface SubscriptionLicensesTableProps {
  /** Current page of license rows from BE (`licenses.data`). */
  licenses: SubscriptionLicense[];
  productNames?: Record<string, string>;
  /** 0-based page index (CustomizedTable). */
  page: number;
  pageSize: number;
  totalRecords: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const SubscriptionLicensesTable = ({
  licenses,
  productNames = {},
  page,
  pageSize,
  totalRecords,
  loading = false,
  onPageChange,
  onPageSizeChange,
}: SubscriptionLicensesTableProps) => {
  const theme = useTheme();

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success('License key copied');
    } catch {
      toast.error('Could not copy license key');
    }
  };

  if (!loading && totalRecords === 0 && licenses.length === 0) {
    return (
      <Typography style={{ fontSize: 13, color: theme.palette.text.secondary }}>
        No license keys on this subscription
      </Typography>
    );
  }

  const rows = licenses.map((license) => ({
    ...license,
    productLabel: productNames[license.productId] || license.productId,
    typeLabel: license.subscriptionType === 'YEARLY' ? 'Yearly' : 'Monthly',
    createdLabel: formatLicenseDate(license.createdAt),
  }));

  const columns = [
    {
      field: 'licenseKey',
      headerName: 'Key',
      sortable: false,
      width: '32%',
      minWidth: '180px',
      render: (value: string): JSX.Element => (
        <Box
          sx={{
            fontSize: 12,
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            color: theme.palette.text.primary,
          }}>
          {value}
        </Box>
      ),
    },
    {
      field: 'productLabel',
      headerName: 'Product / Package',
      sortable: false,
      width: '28%',
      minWidth: '140px',
      render: (value: string): string => value || '—',
    },
    {
      field: 'typeLabel',
      headerName: 'Type',
      sortable: false,
      minWidth: '100px',
      render: (value: string): string => value || '—',
    },
    {
      field: 'createdLabel',
      headerName: 'Created',
      sortable: false,
      minWidth: '120px',
      render: (value: string): string => value || '—',
    },
    {
      field: 'id',
      headerName: '',
      sortable: false,
      minWidth: '56px',
      width: '56px',
      disableTooltip: true,
      render: (_value: string, row: SubscriptionLicense): JSX.Element => (
        <IconButton
          size="small"
          color="primary"
          aria-label="Copy license key"
          onClick={(event) => {
            event.stopPropagation();
            copyKey(row.licenseKey);
          }}>
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ),
    },
  ];

  return (
    <CustomizedTable
      columns={columns}
      data={rows}
      page={page}
      limit={pageSize || DEFAULT_PAGE_SIZE}
      handleLimit={onPageSizeChange}
      handlePage={onPageChange}
      totalRecords={totalRecords}
      loading={loading}
      showPagination
      bodyCellPadding="10px 12px"
      headerCellPadding="10px 12px"
    />
  );
};
